import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { TrendingUp, Users, Eye, MousePointerClick, Instagram, Facebook, Globe, Sparkles } from "lucide-react";

/**
 * Admin analytics tab content. Renders KPI cards, a 30-day pageview/session
 * sparkline, top-content tables and social-click rollups. Reads from
 * `/api/admin/analytics/dashboard`.
 */
export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/analytics/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const sparkPath = useMemo(() => {
    if (!data?.daily?.length) return { sessions: "", pageviews: "" };
    const W = 600;
    const H = 80;
    const values = data.daily;
    const max = Math.max(1, ...values.map((d) => Math.max(d.sessions, d.pageviews)));
    const xStep = W / Math.max(1, values.length - 1);
    const buildPath = (key) =>
      values
        .map((d, i) => `${i === 0 ? "M" : "L"} ${(i * xStep).toFixed(1)} ${(H - (d[key] / max) * H).toFixed(1)}`)
        .join(" ");
    return { sessions: buildPath("sessions"), pageviews: buildPath("pageviews") };
  }, [data]);

  if (loading) return <div className="text-neutral-500" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return <div className="text-rose-600" data-testid="analytics-error">Could not load analytics.</div>;

  const k = data.kpis;
  const kpis = [
    { label: "Sessions (today)", value: k.sessions_today, Icon: Users },
    { label: "Pageviews (today)", value: k.pageviews_today, Icon: Eye },
    { label: "Sessions (30d)", value: k.sessions_30d, Icon: TrendingUp },
    { label: "Pageviews (30d)", value: k.pageviews_30d, Icon: Eye },
    { label: "Unique signed-in (30d)", value: k.unique_users_30d, Icon: Users },
    { label: "Avg pages / session", value: k.avg_pages_per_session, Icon: MousePointerClick },
  ];

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      {/* Highlight reel — single-sentence morning brief */}
      <HighlightReel h={data.highlight} />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-testid="analytics-kpis">
        {kpis.map((kp) => (
          <div key={kp.label} className="bg-white border border-black/5 rounded-2xl p-5">
            <kp.Icon className="w-5 h-5 text-coastal mb-3" />
            <div className="text-2xl font-heading text-navy" data-testid={`kpi-${kp.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
              {Number(kp.value || 0).toLocaleString("en-AU")}
            </div>
            <div className="text-xs text-neutral-500 mt-1">{kp.label}</div>
          </div>
        ))}
      </div>

      {/* Sparkline */}
      <div className="bg-white border border-black/5 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-navy">Last 30 days</h3>
          <div className="flex items-center gap-4 text-xs text-neutral-500">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-coastal" /> Pageviews</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-navy" /> Sessions</span>
          </div>
        </div>
        <svg viewBox="0 0 600 90" className="w-full h-24" data-testid="analytics-sparkline">
          <path d={sparkPath.pageviews} fill="none" stroke="rgb(6 148 162)" strokeWidth="2" />
          <path d={sparkPath.sessions} fill="none" stroke="rgb(13 27 62)" strokeWidth="2" strokeDasharray="3 3" />
        </svg>
        <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
          <span>{data.daily[0]?.date.slice(5)}</span>
          <span>{data.daily[data.daily.length - 1]?.date.slice(5)}</span>
        </div>
      </div>

      {/* Two-column data tables */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <h3 className="font-heading text-lg text-navy mb-4">Most-viewed pages</h3>
          <table className="w-full text-sm" data-testid="analytics-top-paths">
            <thead className="text-xs uppercase text-neutral-400">
              <tr><th className="text-left py-2">Path</th><th className="text-right">Views</th><th className="text-right">Sessions</th></tr>
            </thead>
            <tbody>
              {data.top_paths.length === 0 && (
                <tr><td colSpan={3} className="text-neutral-500 py-3">No data yet — visit pages to populate.</td></tr>
              )}
              {data.top_paths.map((p) => (
                <tr key={p.path} className="border-t border-black/5">
                  <td className="py-2 truncate max-w-[220px] text-navy">{p.path}</td>
                  <td className="text-right">{p.views}</td>
                  <td className="text-right text-neutral-500">{p.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <h3 className="font-heading text-lg text-navy mb-4">Most-viewed venues</h3>
          <table className="w-full text-sm" data-testid="analytics-top-venues">
            <thead className="text-xs uppercase text-neutral-400">
              <tr><th className="text-left py-2">Venue</th><th className="text-right">Views</th></tr>
            </thead>
            <tbody>
              {data.top_venues.length === 0 && (
                <tr><td colSpan={2} className="text-neutral-500 py-3">No venue views yet.</td></tr>
              )}
              {data.top_venues.map((v) => (
                <tr key={v.venue_id} className="border-t border-black/5">
                  <td className="py-2 text-navy">{v.name}</td>
                  <td className="text-right">{v.views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Social + referrers */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <h3 className="font-heading text-lg text-navy mb-4">Outbound social clicks · 30d</h3>
          <div className="grid grid-cols-3 gap-3" data-testid="analytics-social">
            <SocialKpi Icon={Instagram} label="Instagram" value={data.social.instagram} />
            <SocialKpi Icon={Facebook} label="Facebook" value={data.social.facebook} />
            <SocialKpi Icon={Globe} label="Website" value={data.social.website} />
          </div>
          <div className="mt-4 text-xs text-neutral-500">Total {data.social.total}</div>
        </div>

        <div className="bg-white border border-black/5 rounded-2xl p-6">
          <h3 className="font-heading text-lg text-navy mb-4">Top referrers</h3>
          <table className="w-full text-sm" data-testid="analytics-referrers">
            <thead className="text-xs uppercase text-neutral-400">
              <tr><th className="text-left py-2">Source</th><th className="text-right">Visits</th></tr>
            </thead>
            <tbody>
              {data.referrers.length === 0 && (
                <tr><td colSpan={2} className="text-neutral-500 py-3">No external referrers yet.</td></tr>
              )}
              {data.referrers.map((r) => (
                <tr key={r.source} className="border-t border-black/5">
                  <td className="py-2 text-navy truncate max-w-[260px]">{r.source}</td>
                  <td className="text-right">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SocialKpi({ Icon, label, value }) {
  return (
    <div className="border border-black/5 rounded-xl p-4 text-center">
      <Icon className="w-5 h-5 mx-auto text-coastal mb-2" />
      <div className="text-xl font-heading text-navy">{Number(value || 0).toLocaleString("en-AU")}</div>
      <div className="text-[11px] text-neutral-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

/**
 * Single-sentence "morning briefing" of yesterday's activity. If yesterday had
 * zero traffic the reel renders a friendly "quiet day" message instead of
 * misleading blanks.
 */
function HighlightReel({ h }) {
  if (!h) return null;
  const niceDate = (iso) => {
    if (!iso) return "Yesterday";
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" });
  };
  const parts = [];
  if (h.sessions > 0) {
    parts.push(`${h.sessions} session${h.sessions === 1 ? "" : "s"} · ${h.pageviews} pageview${h.pageviews === 1 ? "" : "s"}`);
  }
  if (h.busiest_hour_label) parts.push(`peak ${h.busiest_hour_label}`);
  if (h.top_venue?.name) parts.push(`top venue ${h.top_venue.name} (${h.top_venue.views})`);
  if (h.top_social_click?.venue_name) {
    parts.push(`most clicked ${h.top_social_click.channel} link ${h.top_social_click.venue_name}`);
  }

  return (
    <div
      className="bg-navy text-white rounded-2xl p-6 flex items-start gap-4"
      data-testid="highlight-reel"
    >
      <div className="bg-white/10 rounded-xl p-2.5 shrink-0">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest text-coastal mb-1">{niceDate(h.date)}</div>
        {parts.length === 0 ? (
          <p className="text-base text-white/85" data-testid="highlight-empty">
            Quiet day yesterday — no recorded sessions. Share an inAirlie link to kick traffic off.
          </p>
        ) : (
          <p className="text-base text-white leading-relaxed" data-testid="highlight-sentence">
            {parts.join(" · ")}.
          </p>
        )}
      </div>
    </div>
  );
}
